import { FileText, X } from 'lucide-react';
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../00-MAIN/00-CONTEXT/AuthContext";

function HomeApp() {
  const { user, isAuthenticated, loading: authLoading, error: authError } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); // Pas besoin de loading initial
  const [error, setError] = useState(null);
  const [showBanner, setShowBanner] = useState(true);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [guidedTour, setGuidedTour] = useState({ active: false, currentStep: 0 });
  
  // Ref pour éviter les redirections multiples
  const redirectedRef = useRef(false);

  const tourSteps = [
    { title: "Mon espace personnel", description: "Commencez par configurer votre profil investisseur" },
    { title: "Mon patrimoine", description: "Analysez ensuite votre patrimoine immobilier" },
    { title: "Mon réseau", description: "Développez votre réseau professionnel" },
    { title: "Mes processus", description: "Optimisez vos processus d'investissement" },
    { title: "Mes opérations", description: "Suivez toutes vos opérations immobilières" }
  ];

  useEffect(() => {
    // Éviter les redirections multiples
    if (redirectedRef.current) return;
    
    // Attendre que l'auth loading soit terminé
    if (authLoading) return;
    
    // Si pas authentifié, rediriger UNE SEULE FOIS
    if (!isAuthenticated) {
      redirectedRef.current = true;
      navigate("/login", { replace: true });
      return;
    }
    
    // Si authentifié, arrêter le loading
    setLoading(false);
  }, [isAuthenticated, authLoading, navigate]);

  // Fonction de déconnexion
  const handleLogout = () => {
    localStorage.removeItem("access_token");
    redirectedRef.current = true; // Marquer comme redirigé
    navigate("/login", { replace: true });
  };

  // Affichage du loading pendant l'authentification
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  // Affichage des erreurs
  if (authError || error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-red-500">
          <p>Erreur: {authError || error}</p>
          <button
            onClick={handleLogout}
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  // Si pas authentifié (pendant la redirection)
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p>Redirection vers la connexion...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 px-6 py-8">
      {/* Welcome Banner */}
      {showBanner && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8 relative">
          <button
            onClick={() => setShowBanner(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
          <div className="flex items-start space-x-4">
            <div className="text-2xl">👋</div>
            <div className="w-full">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Bienvenue {user?.prenom || user?.pernom || 'Utilisateur'} !
              </h2>
              <p className="text-gray-600 mb-4">Investissez efficacement et suivez vos performances.</p>

              <div className="bg-blue-50 rounded-lg p-4 mb-4 w-full">
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 mb-1">Selery vous accompagne de A à Z dans votre activité immobilière</h3>
                    <p className="text-sm text-gray-600 mb-3">Découvrez Selery pas à pas grace à nos tutoriels </p>
                    <button
                      onClick={() => {
                        setShowRegistrationModal(false);
                        setGuidedTour({ active: true, currentStep: 0 });
                      }}
                      className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
                    >
                      Démarrez la prise en main
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cinq cadres pour les pages de la sidebar */}
      <div className="grid grid-cols-5 gap-6">
        {/* Cadre 1 - Mon espace personnel */}
        <div className={`bg-white rounded-lg border p-6 h-[650px] flex flex-col relative ${guidedTour.active && guidedTour.currentStep === 0
          ? 'border-teal-500 border-2 shadow-lg shadow-teal-200'
          : 'border-gray-200'
          }`}>
          {guidedTour.active && guidedTour.currentStep === 0 && (
            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-teal-600 text-white px-3 py-1 rounded-lg text-xs font-medium">
              Étape 1/5
            </div>
          )}
          <div className="text-center">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#c8d7d6' }}>
              <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">1</span>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-6 text-sm">Mon espace personnel</h3>

            {/* Zone vidéo */}
            <div className="w-full h-48 bg-black rounded-lg flex items-center justify-center">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-white text-sm">▶</span>
              </div>
            </div>
          </div>

          {/* 3 bullet points explicatifs - juste après la vidéo */}
          <div className="text-left mt-4 space-y-1">
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Configurez votre profil investisseur</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Définissez vos objectifs financiers</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Personnalisez votre tableau de bord</p>
            </div>
          </div>

          {/* Spacer pour pousser le bouton vers le bas */}
          <div className="flex-grow"></div>

          <button
            onClick={() => {
              if (guidedTour.active && guidedTour.currentStep === 0) {
                setGuidedTour({ active: true, currentStep: 1 });
              }
            }}
            className="w-full text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-gray-700 transition-colors"
            style={{ backgroundColor: '#c8d7d6' }}
          >
            Gérer mon profil
          </button>
        </div>

        {/* Cadre 2 - Mon patrimoine */}
        <div className={`bg-white rounded-lg border p-6 h-[650px] flex flex-col relative ${guidedTour.active && guidedTour.currentStep === 1
          ? 'border-teal-500 border-2 shadow-lg shadow-teal-200'
          : 'border-gray-200'
          }`}>
          {guidedTour.active && guidedTour.currentStep === 1 && (
            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-teal-600 text-white px-3 py-1 rounded-lg text-xs font-medium">
              Étape 2/5
            </div>
          )}
          <div className="text-center">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#c8d7d6' }}>
              <div className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">2</span>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-6 text-sm">Mon patrimoine</h3>

            {/* Zone vidéo */}
            <div className="w-full h-48 bg-black rounded-lg flex items-center justify-center">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-white text-sm">▶</span>
              </div>
            </div>
          </div>

          {/* 3 bullet points explicatifs - juste après la vidéo */}
          <div className="text-left mt-4 space-y-1">
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Évaluez la valeur de vos biens</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Analysez la rentabilité par bien</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Suivez l'évolution du marché</p>
            </div>
          </div>

          {/* Spacer pour pousser le bouton vers le bas */}
          <div className="flex-grow"></div>

          <button
            onClick={() => {
              if (guidedTour.active && guidedTour.currentStep === 1) {
                setGuidedTour({ active: true, currentStep: 2 });
              }
            }}
            className="w-full text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-gray-700 transition-colors"
            style={{ backgroundColor: '#c8d7d6' }}
          >
            Analyser patrimoine
          </button>
        </div>

        {/* Cadre 3 - Mon réseau */}
        <div className={`bg-white rounded-lg border p-6 h-[650px] flex flex-col relative ${guidedTour.active && guidedTour.currentStep === 2
          ? 'border-teal-500 border-2 shadow-lg shadow-teal-200'
          : 'border-gray-200'
          }`}>
          {guidedTour.active && guidedTour.currentStep === 2 && (
            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-teal-600 text-white px-3 py-1 rounded-lg text-xs font-medium">
              Étape 3/5
            </div>
          )}
          <div className="text-center">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#c8d7d6' }}>
              <div className="w-6 h-6 bg-gray-800 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">3</span>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-6 text-sm">Mon réseau</h3>

            {/* Zone vidéo */}
            <div className="w-full h-48 bg-black rounded-lg flex items-center justify-center">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-white text-sm">▶</span>
              </div>
            </div>
          </div>

          {/* 3 bullet points explicatifs - juste après la vidéo */}
          <div className="text-left mt-4 space-y-1">
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Connectez-vous aux professionnels</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Trouvez des partenaires fiables</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Échangez avec d'autres investisseurs</p>
            </div>
          </div>

          {/* Spacer pour pousser le bouton vers le bas */}
          <div className="flex-grow"></div>

          <button
            onClick={() => {
              if (guidedTour.active && guidedTour.currentStep === 2) {
                setGuidedTour({ active: true, currentStep: 3 });
              }
            }}
            className="w-full text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-gray-700 transition-colors"
            style={{ backgroundColor: '#c8d7d6' }}
          >
            Gérer réseau
          </button>
        </div>

        {/* Cadre 4 - Mes processus */}
        <div className={`bg-white rounded-lg border p-6 h-[650px] flex flex-col relative ${guidedTour.active && guidedTour.currentStep === 3
          ? 'border-teal-500 border-2 shadow-lg shadow-teal-200'
          : 'border-gray-200'
          }`}>
          {guidedTour.active && guidedTour.currentStep === 3 && (
            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-teal-600 text-white px-3 py-1 rounded-lg text-xs font-medium">
              Étape 4/5
            </div>
          )}
          <div className="text-center">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#c8d7d6' }}>
              <div className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">4</span>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-6 text-sm">Mes processus</h3>

            {/* Zone vidéo */}
            <div className="w-full h-48 bg-black rounded-lg flex items-center justify-center">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-white text-sm">▶</span>
              </div>
            </div>
          </div>

          {/* 3 bullet points explicatifs - juste après la vidéo */}
          <div className="text-left mt-4 space-y-1">
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Automatisez vos workflows</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Optimisez vos recherches de biens</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Standardisez vos négociations</p>
            </div>
          </div>

          {/* Spacer pour pousser le bouton vers le bas */}
          <div className="flex-grow"></div>

          <button
            onClick={() => {
              if (guidedTour.active && guidedTour.currentStep === 3) {
                setGuidedTour({ active: true, currentStep: 4 });
              }
            }}
            className="w-full text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-gray-700 transition-colors"
            style={{ backgroundColor: '#c8d7d6' }}
          >
            Optimiser processus
          </button>
        </div>

        {/* Cadre 5 - Mes opérations */}
        <div className={`bg-white rounded-lg border p-6 h-[650px] flex flex-col relative ${guidedTour.active && guidedTour.currentStep === 4
          ? 'border-teal-500 border-2 shadow-lg shadow-teal-200'
          : 'border-gray-200'
          }`}>
          {guidedTour.active && guidedTour.currentStep === 4 && (
            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-teal-600 text-white px-3 py-1 rounded-lg text-xs font-medium">
              Étape 5/5
            </div>
          )}
          <div className="text-center">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#c8d7d6' }}>
              <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">5</span>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-6 text-sm">Mes opérations</h3>

            {/* Zone vidéo */}
            <div className="w-full h-48 bg-black rounded-lg flex items-center justify-center">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-white text-sm">▶</span>
              </div>
            </div>
          </div>

          {/* 3 bullet points explicatifs - juste après la vidéo */}
          <div className="text-left mt-2 space-y-1">
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Suivez vos acquisitions en cours</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Gérez vos ventes et cessions</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs text-gray-600">Planifiez vos investissements futurs</p>
            </div>
          </div>

          {/* Spacer pour pousser le bouton vers le bas */}
          <div className="flex-grow"></div>

          <button
            onClick={() => {
              if (guidedTour.active && guidedTour.currentStep === 4) {
                setGuidedTour({ active: false, currentStep: 0 });
                alert('Félicitations ! Vous avez terminé la prise en main de Selery.');
              }
            }}
            className="w-full text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-gray-700 transition-colors"
            style={{ backgroundColor: '#c8d7d6' }}
          >
            Suivre opérations
          </button>
        </div>
      </div>

      {/* Guided Tour Overlay */}
      {guidedTour.active && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 max-w-md pointer-events-auto shadow-xl">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {tourSteps[guidedTour.currentStep].title}
              </h3>
              <p className="text-gray-600 mb-4">
                {tourSteps[guidedTour.currentStep].description}
              </p>
              <div className="flex items-center justify-center space-x-2 mb-4">
                {tourSteps.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full ${index === guidedTour.currentStep ? 'bg-teal-600' : 'bg-gray-300'
                      }`}
                  />
                ))}
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Cliquez sur le bouton du cadre mis en surbrillance pour continuer
              </p>
              <button
                onClick={() => setGuidedTour({ active: false, currentStep: 0 })}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Arrêter le parcours
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration Modal */}
      {showRegistrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Enregistrement PDP</h3>
              <button
                onClick={() => setShowRegistrationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Commencez votre enregistrement en tant que Plateforme de Dématérialisation Partenaire.
              Cette démarche est gratuite et sans engagement.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowRegistrationModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  setShowRegistrationModal(false);
                  setGuidedTour({ active: true, currentStep: 0 });
                }}
                className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
              >
                Commencer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomeApp;